/**
 * Claim-the-daily-reward action, packaged for reuse outside the global menu (specs/profile-page).
 *
 * Wraps the shared `useEconomyStore.claimDaily` with the same UX the menu applies: guest guard,
 * success sound (tiered by streak), and snackbar on failure. State itself lives entirely in the
 * store, so this and the menu's DailyRewardModal claim the same source of truth — claiming here
 * updates there and vice-versa, and a double-claim is impossible.
 */

import { useCallback } from 'react';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useSound } from '../../shared/hooks/useSound';
import { useSnackbar } from '../../shared/hooks/useSnackbar';
import { useGuestGuard } from '../../shared/hooks/useGuestGuard';
import { GUEST_AUTH_ACTIONS } from '../../shared/hooks/useGuestGuard.constants';
import { getCountdownDisplay } from '../../challenges/helpers/countdownToDate.helper';
import { getDailyClaimSuccessSoundId } from '../helpers/dailyClaimGameSound.helper';

type ApiClientFn = (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>;

export function useDailyClaimAction(): () => Promise<void> {
  const apiClient = useApiClient();
  const claimDaily = useEconomyStore((s) => s.claimDaily);
  const { playSound } = useSound();
  const { showSnackbar } = useSnackbar();
  const { requireAuth } = useGuestGuard();

  return useCallback(async () => {
    if (!requireAuth(GUEST_AUTH_ACTIONS.DAILY_REWARD)) return;
    const preClaim = useEconomyStore.getState();
    const successSoundId = getDailyClaimSuccessSoundId(preClaim.dailyClaimStreak, preClaim.lastDailyClaimAt);

    const result = await claimDaily(apiClient as ApiClientFn);
    if (result.success) {
      playSound(successSoundId);
      return;
    }
    if (result.error === 'SIGN_IN_REQUIRED') {
      void requireAuth(GUEST_AUTH_ACTIONS.DAILY_REWARD);
      return;
    }
    if (result.retryAfterMs) {
      showSnackbar(
        `Available in ${getCountdownDisplay(new Date(Date.now() + result.retryAfterMs))}. Coins reset at UTC midnight.`,
        'info',
        'Daily Claim',
      );
    } else {
      showSnackbar(result.error ?? 'Could not claim coins right now.', 'error', 'Daily Claim');
    }
  }, [apiClient, claimDaily, playSound, showSnackbar, requireAuth]);
}
